import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { BodyTypeService } from 'src/app/services/body-type/body-type.service';
import { BodyTypeModalService } from 'src/app/services/body-type-modal/body-type-modal.service';
import { VehicleTypeService } from 'src/app/services/vehicle-type/vehicle-type.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-body-type',
  templateUrl: './add-edit-body-type.component.html',
  styleUrls: ['./add-edit-body-type.component.scss']
})
export class AddEditBodyTypeComponent extends DestructorComponent implements OnInit {

  @Output() bodyTypeAdded = new EventEmitter<void>();
  @Output() bodyTypeUpdated = new EventEmitter<void>();

  bodyTypeForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedBodyType: any = null;

  // Vehicle types for dropdown
  vehicleTypes: any[] = [];
  isLoadingVehicleTypes: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _bodyTypeService: BodyTypeService,
    private _bodyTypeModalService: BodyTypeModalService,
    private _vehicleTypeService: VehicleTypeService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadVehicleTypes();
  }

  private initializeForm(): void {
    this.bodyTypeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      type: ['', [Validators.required]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_body_type_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceBodyType = this._bodyTypeModalService.getSelectedBodyType();
        if (serviceBodyType) {
          this.isEditMode = true;
          this.selectedBodyType = serviceBodyType;
          this.populateForm(this.selectedBodyType);
        } else {
          this.isEditMode = false;
          this.selectedBodyType = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._bodyTypeModalService.clearSelectedBodyType();
        this.resetForm();
      });
    }
  }

  private loadVehicleTypes(): void {
    this.isLoadingVehicleTypes = true;
    this.subscriptions.push(
      this._vehicleTypeService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.vehicleTypes = response.data.data || response.data || [];
          } else {
            this.vehicleTypes = [];
          }
          this.isLoadingVehicleTypes = false;
        },
        error: (error) => {
          console.error('Error loading vehicle types:', error);
          this.vehicleTypes = [];
          this.isLoadingVehicleTypes = false;
        }
      })
    );
  }

  private populateForm(bodyType: any): void {
    if (bodyType) {
      this.bodyTypeForm.patchValue({
        name: bodyType.name || '',
        type: bodyType.type?._id || bodyType.type?.id || bodyType.type || ''
      });
    }
  }

  private resetForm(): void {
    this.bodyTypeForm.reset();
    this.bodyTypeForm.patchValue({
      name: '',
      type: ''
    });
    this.isLoading = false;
  }

  // Getter for easy access to form controls
  get name() { return this.bodyTypeForm.get('name'); }
  get type() { return this.bodyTypeForm.get('type'); }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  onSubmit(): void {
    if (this.bodyTypeForm.invalid) {
      this.markFormGroupTouched(this.bodyTypeForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      name: this.bodyTypeForm.get('name')?.value,
      type: this.bodyTypeForm.get('type')?.value
    };

    if (this.isEditMode) {
      this.updateBodyType(formData);
    } else {
      this.addBodyType(formData);
    }
  }

  private addBodyType(formData: any): void {
    this.subscriptions.push(
      this._bodyTypeService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Body type added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.bodyTypeAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add body type.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding body type:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding the body type.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateBodyType(formData: any): void {
    const bodyTypeId = this.selectedBodyType._id || this.selectedBodyType.id;
    this.subscriptions.push(
      this._bodyTypeService.UpdateData(bodyTypeId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Body type updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.bodyTypeUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update body type.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error updating body type:', error);
          this.isLoading = false;

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_body_type_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }
}
